import { useMemo } from 'react';

import type { DataTableScrollAnchorTarget } from './DataTableLayout';
import { findVerticalScrollParent } from './findVerticalScrollParent';

type Options = {
  isVirtualized: boolean;
  scrollContainer: 'self' | 'ancestor';
  containerElement: HTMLElement | null;
  tableElement: HTMLElement | null;
  stickyHeaderOffset: number | undefined;
};

const viewportTop = (element: HTMLElement) =>
  element === document.scrollingElement ? 0 : element.getBoundingClientRect().top;

export function useScrollAnchorTarget({
  isVirtualized,
  scrollContainer,
  containerElement,
  tableElement,
  stickyHeaderOffset,
}: Options): DataTableScrollAnchorTarget | undefined {
  return useMemo(() => {
    if (!isVirtualized || !tableElement) {
      return undefined;
    }
    const table = tableElement;
    const findScroller = () =>
      scrollContainer === 'self' ? table : containerElement ? findVerticalScrollParent(containerElement) : null;
    return {
      getViewportTop: () => {
        const scroller = findScroller();
        if (!scroller) {
          return undefined;
        }
        return scroller === table
          ? table.scrollTop
          : viewportTop(scroller) + (stickyHeaderOffset ?? 0) - table.getBoundingClientRect().top;
      },
      scrollBy: (delta) => {
        const scroller = findScroller();
        if (scroller) {
          scroller.scrollTop += delta;
        }
      },
    };
  }, [containerElement, isVirtualized, scrollContainer, stickyHeaderOffset, tableElement]);
}
